import { z } from '@customerio/hydra-validation';
import type { Newsletter as ApiNewsletter, operations } from '@customerio/hydra-ui-api';
import { defineEntity, readSchema } from '../shared/define-entity';

export type Newsletter = Omit<ApiNewsletter, 'tag_ids' | 'template_ids'> & {
  tag_ids: string[];
  template_ids: string[];
};

const NewsletterUserFromApi = z.object({
  id: z.string(),
  email: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  profile_image_url: z.string().nullish(),
});

const NewsletterFromApi = readSchema<Newsletter>(
  z.looseObject({
    id: z.string(),
    created_by_user: NewsletterUserFromApi.nullish().transform((user) => user ?? undefined),
    updated_by_user: NewsletterUserFromApi.nullish().transform((user) => user ?? undefined),
    is_paused: z
      .boolean()
      .nullish()
      .transform((value) => value ?? false),
    tag_ids: z
      .array(z.string())
      .nullish()
      .transform((ids) => ids ?? []),
    template_ids: z
      .array(z.string())
      .nullish()
      .transform((ids) => ids ?? []),
  }),
);

/**
 * The status filter on the Broadcasts newsletters list. `all` is a real
 * sentinel the backend understands, not an "omit the param" marker: its list
 * handler leaves the status filter unapplied for both `""` and `"all"`, and
 * its sort explicitly pairs the two when picking the sent-descending
 * ordering. So the selected value goes on the wire as-is.
 */
export type NewsletterStatusFilter = 'all' | 'drafted' | 'scheduled' | 'sent';

/** The channel filter's "no filter" sentinel — also understood server-side. */
export const ALL_NEWSLETTER_CHANNELS = 'all';

/** The status filter's default. */
export const ALL_NEWSLETTER_STATUSES = 'all';

/**
 * Filters on the Broadcasts newsletters list.
 *
 * `page` is 1-based here and converted at the request boundary, matching the
 * campaign list params. Id arrays are strings because every id in this layer
 * is a string; they are converted to the numbers the wire wants when the
 * query is built.
 */
export interface NewsletterListParams {
  page: number;
  /** A channel name, or `all` for no channel filter. */
  channel?: string;
  type?: NewsletterStatusFilter;
  /** Server-side, case-insensitive substring match on newsletter names. */
  search?: string;
  sort?: string;
  asc?: boolean;
  /** Inclusive lower bound, unix seconds, on the date the sort key selects. */
  startTime?: number;
  /** Inclusive upper bound, unix seconds. */
  endTime?: number;
  sendToUnsubscribed?: boolean;
  subscriptionTopicId?: number;
  tagIds?: readonly string[];
  objectTypeIds?: readonly string[];
  goalIds?: readonly string[];
}

/**
 * `undefined` is part of the params union so the unfiltered workspace-wide
 * read (`getNewslettersQueryOptions`) keeps its own cache slot alongside the
 * filtered Broadcasts list. Both sit under `newsletterKeys.lists`, so one
 * prefix invalidation after a mutation covers them together.
 */
/** @public */
export const newsletter = defineEntity<
  Newsletter,
  Newsletter,
  NewsletterListParams | undefined,
  undefined,
  'workspace'
>({
  name: 'newsletter',
  scope: 'workspace',
  pk: (n) => n.id,
  schema: { read: NewsletterFromApi },
});

export const newsletterKeys = newsletter.keys;

/** Optional filters on the newsletter catalog reads. */
export interface NewsletterCatalogScope {
  /** Case-insensitive substring match on newsletter names. */
  search?: string;
}

/**
 * One catalog slot (the server-paged, optionally searched picker list). Kept
 * under the entity's `all` prefix so entity-wide invalidation covers it.
 */
export const newsletterCatalogKey = (workspaceId: number, scope: NewsletterCatalogScope) =>
  [...newsletterCatalogPrefix(workspaceId), scope] as const;

/**
 * Every catalog slot for the workspace, whatever the scope. Lets a mutation
 * invalidate the catalogs without reaching the entity's `all` prefix, which
 * would refetch every cached newsletter detail too.
 */
export const newsletterCatalogPrefix = (workspaceId: number) =>
  [...newsletterKeys.all(workspaceId), 'catalog'] as const;

/**
 * A newsletter's schedule, kept under its detail slot so anything that invalidates the
 * newsletter invalidates its schedule with it.
 */
export const newsletterScheduleKey = (workspaceId: number, newsletterId: string) =>
  [...newsletterKeys.detail(workspaceId, newsletterId), 'schedule'] as const;

/** How far a newsletter's send has got, kept under its detail slot for the same reason as its schedule. */
export const newsletterSendStatusKey = (workspaceId: number, newsletterId: string) =>
  [...newsletterKeys.detail(workspaceId, newsletterId), 'send-status'] as const;

/** A newsletter's delivery metrics, kept under its detail slot for the same reason as its schedule. */
export const newsletterMetricKey = (workspaceId: number, newsletterId: string) =>
  [...newsletterKeys.detail(workspaceId, newsletterId), 'metric'] as const;

export type NewsletterMetricsPeriod = NonNullable<
  operations['get-v1-environments-environment_id-newsletters-id-metrics']['parameters']['query']
>['period'];

export const newsletterMetricsKey = (
  workspaceId: number,
  newsletterId: string,
  period: NewsletterMetricsPeriod = 'days',
) => [...newsletterKeys.detail(workspaceId, newsletterId), 'reports-metrics', period] as const;

export type TemplateTrackedLinksQuery = NonNullable<
  operations['get-v1-environments-environment_id-templates-template_id-tracked_link_metric']['parameters']['query']
>;

export const newsletterTrackedLinksKey = (workspaceId: number, newsletterId: string) =>
  [...newsletterKeys.detail(workspaceId, newsletterId), 'tracked-links'] as const;

export const templateTrackedLinksKey = (
  workspaceId: number,
  newsletterId: string,
  templateId: string,
  query: TemplateTrackedLinksQuery,
) => [...newsletterTrackedLinksKey(workspaceId, newsletterId), templateId, query] as const;

export const newsletterTemplateMetricsKey = (workspaceId: number, newsletterId: string) =>
  [...newsletterKeys.detail(workspaceId, newsletterId), 'template-metrics'] as const;

export const newsletterTrackedResponseMetricsKey = (workspaceId: number, newsletterId: string) =>
  [...newsletterKeys.detail(workspaceId, newsletterId), 'tracked-response-metrics'] as const;

/**
 * A newsletter's send timing for one picked window. The window's bounds move with
 * "now", so the key names the selection, not the seconds sent.
 */
export const newsletterSendTimingKey = (workspaceId: number, newsletterId: string, selection: string) =>
  [...newsletterKeys.detail(workspaceId, newsletterId), 'send-timing', selection] as const;

export function parseNewsletter(raw: ApiNewsletter): Newsletter {
  return newsletter.parse(raw);
}
